import {Controller} from '@hotwired/stimulus';

const RELEASE_DELAY = 10000;

/**
 * Modale qui confirme une action en nommant l'objet (`modal.md`, variants `destructive` et `confirm`) :
 * « Supprimer la réduction de 10 % ? ».
 *
 * Posé sur une `.modal` (avec un id), rendue par le composant Twig `ConfirmDialog`, que le déclencheur ouvre
 * par son balisage Bootstrap (`data-bs-toggle="modal"`, `data-bs-target="#id"`) : Bootstrap garde le focus
 * dans la modale, la ferme à Échap et rend le focus au déclencheur. Les values `title` et `consequence`
 * portent `{name}`, remplacé par le nom de l'objet, que le déclencheur donne par `data-confirm-dialog-name`,
 * ou par `data-confirm-dialog-name-from` (id du champ qui le porte). À l'ouverture, le focus va sur
 * « Annuler » (`destructive`, pour qu'un Entrée réflexe ne détruise rien) ou sur le titre (`confirm`).
 *
 * Le bouton d'action (`confirm-dialog#confirm`) émet `confirm-dialog:confirmed` sur le déclencheur. Si la page
 * ne l'annule pas, et que le déclencheur porte `data-confirm-dialog-url`, la page suit cette URL ; s'il porte
 * `data-confirm-dialog-submit`, son formulaire est envoyé. La modale reste alors ouverte, le bouton en
 * chargement, jusqu'au départ de la page ; au bout de dix secondes, elle redevient refermable, l'action restant
 * désactivée. Sinon, la modale se ferme. Le bouton d'action ne porte pas `data-bs-dismiss`.
 *
 * Un nom lu dans un champ encore vide désigne un objet pas encore enregistré, que la suppression ne fait
 * pas perdre : l'événement part aussitôt, sans modale.
 */
export default class extends Controller {
  static targets = ['title', 'consequence', 'cancel', 'action'];

  static values = {
    title: String,
    consequence: String,
    variant: {type: String, default: 'destructive'},
  };

  connect() {
    this.prepare = this.prepare.bind(this);
    this.placeFocus = this.placeFocus.bind(this);
    this.keepOpen = this.keepOpen.bind(this);
    this.restoreFocus = this.restoreFocus.bind(this);
    this.skipUnsaved = this.skipUnsaved.bind(this);
    this.reset = this.reset.bind(this);
    this.release = this.release.bind(this);
    this.restore = this.restore.bind(this);
    this.element.addEventListener('show.bs.modal', this.prepare);
    this.element.addEventListener('shown.bs.modal', this.placeFocus);
    this.element.addEventListener('hide.bs.modal', this.keepOpen);
    this.element.addEventListener('hidden.bs.modal', this.restoreFocus);
    // en capture sur `window`, avant le data-API de Bootstrap, lui-même en capture sur `document` :
    // annuler `show.bs.modal` laisserait la modale de Bootstrap 5.0 bloquée, elle ne s'ouvrirait plus
    window.addEventListener('click', this.skipUnsaved, true);
    window.addEventListener('pageshow', this.restore);
  }

  disconnect() {
    this.element.removeEventListener('show.bs.modal', this.prepare);
    this.element.removeEventListener('shown.bs.modal', this.placeFocus);
    this.element.removeEventListener('hide.bs.modal', this.keepOpen);
    this.element.removeEventListener('hidden.bs.modal', this.restoreFocus);
    window.removeEventListener('click', this.skipUnsaved, true);
    window.removeEventListener('pageshow', this.restore);
    clearTimeout(this.releaseTimer);
  }

  skipUnsaved(event) {
    const trigger = event.target.closest?.(`[data-bs-target="#${this.element.id}"]`);

    if (!trigger || trigger.dataset.confirmDialogNameFrom === undefined || nameOf(trigger) !== '') {
      return;
    }

    event.stopPropagation();
    this.dispatch('confirmed', {target: trigger});
  }

  prepare(event) {
    this.reset();
    this.trigger = event.relatedTarget ?? null;

    if (!this.trigger) {
      return;
    }

    const name = nameOf(this.trigger);
    // une fonction de remplacement : un « $ » du nom ne doit pas être lu comme un motif
    this.titleTarget.textContent = this.titleValue.replace('{name}', () => name);

    if (this.hasConsequenceTarget && this.hasConsequenceValue) {
      this.consequenceTarget.textContent = this.consequenceValue.replace('{name}', () => name);
    }
  }

  placeFocus() {
    if (this.variantValue === 'confirm' || !this.hasCancelTarget) {
      this.titleTarget.focus();
    } else {
      this.cancelTarget.focus();
    }
  }

  keepOpen(event) {
    if (this.pending && !this.closable) {
      event.preventDefault();
    }
  }

  // Bootstrap ne rend le focus qu'à un déclencheur visible : celui d'un menu déroulant refermé le rend au menu
  restoreFocus() {
    if (!this.trigger || this.trigger.offsetParent !== null) {
      return;
    }

    this.trigger.closest('.dropdown, .btn-group')?.querySelector('[data-bs-toggle="dropdown"]')?.focus();
  }

  confirm() {
    if (this.pending || !this.trigger) {
      return;
    }

    const url = this.trigger.dataset.confirmDialogUrl;
    const form = this.trigger.dataset.confirmDialogSubmit !== undefined ? this.trigger.form : null;

    // un formulaire invalide ne part pas : la modale se ferme sur le champ en erreur au lieu d'attendre un départ
    if (form && !form.checkValidity()) {
      this.element.addEventListener('hidden.bs.modal', () => form.reportValidity(), {once: true});
      this.close();
      return;
    }

    const confirmed = this.dispatch('confirmed', {target: this.trigger});

    if (confirmed.defaultPrevented || (url === undefined && !form)) {
      // un second clic pendant la fermeture ne doit pas confirmer deux fois
      this.trigger = null;
      this.close();
      return;
    }

    this.pending = true;
    // une réponse qui ne quitte pas la page (fichier téléchargé, 204) ne doit pas laisser la modale bloquée :
    // elle redevient refermable, mais l'action reste désactivée, sa requête a pu partir
    this.releaseTimer = setTimeout(this.release, RELEASE_DELAY);

    if (this.hasActionTarget) {
      this.actionTarget.classList.add('is-loading');
      this.actionTarget.setAttribute('aria-disabled', 'true');
    }

    if (form) {
      form.requestSubmit();
    } else {
      window.location.assign(url);
    }
  }

  // le bouton d'action ne porte pas `data-bs-dismiss` : Bootstrap l'écoute en capture sur la modale, avant
  // `confirm`, et fermerait la modale qui doit rester ouverte le temps de quitter la page
  close() {
    this.element.querySelector('[data-bs-dismiss="modal"]')?.click();
  }

  // retour arrière vers une page gardée en cache : l'action est partie, sa confirmation se referme
  restore(event) {
    this.reset();

    if (event.persisted && this.element.classList.contains('show')) {
      this.close();
    }
  }

  release() {
    this.closable = true;
  }

  reset() {
    clearTimeout(this.releaseTimer);
    this.pending = false;
    this.closable = false;

    if (this.hasActionTarget) {
      this.actionTarget.classList.remove('is-loading');
      this.actionTarget.removeAttribute('aria-disabled');
    }
  }
}

function nameOf(trigger) {
  const {confirmDialogName, confirmDialogNameFrom} = trigger.dataset;

  if (confirmDialogNameFrom === undefined) {
    return (confirmDialogName ?? '').trim();
  }

  return (document.getElementById(confirmDialogNameFrom)?.value ?? '').trim();
}
